import { Store } from '@reduxjs/toolkit';
import { groupFilesbyParentId } from './titleUtils';
import { deepCopy, deleteObjectKeysByString, deepMerge } from './utils';
import { RJSFSchema } from '@rjsf/utils';
import { nanoid } from 'nanoid';
import { isEqual } from 'lodash';
import { OrderPlacementConfig } from '@/__configs/config_types';

let store: Store;
export const injectStore = (_store: Store) => {
  store = _store;
};

// used in profile/getWorkflowTemplate
export const parseWorkflowSchema = (schema: RJSFSchema) => {
  if (!schema?.properties) return null;
  const schemaProps = deepCopy(schema.properties);

  // find the enums for dropdowns and save labels for easy access
  Object.keys(schemaProps).forEach((key) => {
    const enums = schemaProps[key].enum;
    const enumNames = schemaProps[key].enumNames || enums;
    if (enumNames) {
      schemaProps[key].optionLabels = {};
      schemaProps[key].options = [];
      schemaProps[key].type = 'select';
      enums.forEach((x: string, i: number) => {
        schemaProps[key].optionLabels[x] = enumNames[i];
        schemaProps[key].options.push({ value: x, label: enumNames[i] });
      });
    }
  });

  return schemaProps;
};

const buildComponentizedOrder = (values: any, outputVersions: any[]) => {
  const { jobs, managed_by, billable_customer, ...rest } = values;
  const due_date = typeof jobs?.[0].due_date === 'string' ? jobs?.[0].due_date : jobs?.[0].due_date.toISOString();
  const order = {
    ...rest,
    managed_by: managed_by?.id,
    billable_customer: billable_customer?.id,
    order_requests: outputVersions.map((ov) => {
      const groupedByVersionId = groupFilesbyParentId(ov.selections);
      const { profile, ...job } = jobs[0];
      return {
        ...job, // bc we only allow a single profile selection for componentized
        profile_instance: {
          id: profile?.id,
          data: profile?.data
        },
        due_date,
        source_inputs: {
          versions: Object.keys(groupedByVersionId).map((versionId) => ({
            id: versionId,
            components: (groupedByVersionId[versionId as keyof typeof groupedByVersionId] as any[]).map((file) => ({
              id: file.id,
              label: file.label
            }))
          }))
        },
        title_inputs: ov.title_inputs,
        primary_version_id: ov.primary_version ? ov.primary_version : ov.selections[0].version_external_id
      };
    })
  };
  deleteObjectKeysByString(order, '');
  return order;
};

const buildVersionOrder = (values: any, profileJobs: any, addlFields: OrderPlacementConfig['addlFields']) => {
  const { managed_by, billable_customer, ...rest } = values;
  const jobs = Object.values(profileJobs);
  const order = {
    ...rest,
    managed_by: managed_by?.id,
    billable_customer: billable_customer?.id,
    order_requests: (jobs as any[]).map((job) => {
      const { order_profile_inputs, order_spec_inputs } = job;
      const addlProfileFields = calculateAdditionalProfileFieldValues(job, addlFields);

      return {
        profile_id: job.profile?.id,
        profile_instance: {
          id: job.profile?.id,
          data: job.profile?.data
        },
        primary_version_id: job.version.id,
        due_date: job.due_date,
        embargo_date: job.embargo_date,
        instance_inputs: job.instance_inputs,
        ...addlProfileFields,
        ...(order_profile_inputs ? { order_profile_inputs } : {}),
        ...(order_spec_inputs ? { order_spec_inputs } : {}),
        workflow_version: job.workflow_version || undefined
      };
    })
  };
  // these are the profiles that we store as jobs in the form
  // we don't need it in this payload
  delete order.jobs;
  return order;
};

export const buildV2Order = (values: any, jobs = {}, outputVersions: any, addlFields: OrderPlacementConfig['addlFields']) => {
  const componentizedOrder = Object.keys(jobs)?.length ? false : true;
  const orderFields = componentizedOrder ? buildComponentizedOrder(values, outputVersions) : buildVersionOrder(values, jobs, addlFields);
  return orderFields;
};

export const cleanDates = (orderRequests: any[], disableDueDate: boolean, disableEmbargoDate: boolean) => {
  if (!orderRequests?.length) return orderRequests;
  return orderRequests.map((orderRequest) => {
    if (disableDueDate) {
      delete orderRequest.due_date;
    }
    if (disableEmbargoDate) {
      delete orderRequest.embargo_date;
    }
    return orderRequest;
  });
};

// this validates and returns the errors for the fields on jobs on step 3
// we receive the field errors and dispatch `setValidationErrors` on each fieldError returned
const jobValidationFields = ['customer', 'profile', 'due_date'];
export const validateV2Jobs = (jobs: any) => {
  const fieldErrors: any[] = [];
  Object.keys(jobs).forEach((jobId) => {
    jobValidationFields.forEach((field) => {
      if (!jobs[jobId][field]) {
        fieldErrors.push({ jobId, field });
      }
    });
  });
  return fieldErrors;
};

// in the inventory slice (for opv2), we save version keys for quick lookup
// we also save them to the draft so that the keys can be used when resuming the draft
// this function cleans up the keys so that the ones we no longer need for the order are removed
// note version keys are only needed for the advanced flow
export const getCleanedVersionKeys = (jobs: any) => {
  const versionKeys = store.getState().orderPlacement?.versionKeys;
  const versionSelections = store.getState().orderPlacement?.versionSelections; // simple flow selections
  const outputVersions = store.getState().orderPlacement?.outputVersions; // advanced flow selections
  const inventorySelections = store.getState().orderPlacement?.inventorySelections;
  const versionsToKeep: any[] = [];

  // simple flow
  if (versionSelections?.length) {
    versionSelections.forEach((version: any) => {
      versionsToKeep.push(version.program_id);
    });

    if (jobs && Object.keys(jobs).length) {
      const jobsArray = Object.values(jobs);
      // get output version ids from jobs
      jobsArray.forEach((job: any) => {
        const jobOutputVersionId = job.instance_inputs?.output_version_id;
        if (jobOutputVersionId) {
          versionsToKeep.push(jobOutputVersionId);
          // look up the version keys for the jobs output versions to push its parent program id to versionsToKeep
          const parentId = versionKeys[jobOutputVersionId]?.program_id;
          versionsToKeep.push(parentId);
        }
      });
    }
  }
  // advanced flow
  else if (outputVersions.length) {
    outputVersions.forEach((ov: any) => {
      versionsToKeep.push(ov.primary_version);
      if (ov.title_inputs?.output_version_id) {
        versionsToKeep.push(ov.title_inputs?.output_version_id);
      }
      ov.selections.forEach((v: any) => {
        versionsToKeep.push(v.version_id);
        if (!versionsToKeep.includes(v.parent_id)) {
          versionsToKeep.push(v.parent_id);
        }
      });
    });
  } else {
    inventorySelections.forEach((inventory: any) => versionsToKeep.push(inventory.version_external_id));
  }

  // remove keys from versionKeys object that do not have keys in the primaryVersions array
  const cleanVersionKeys = Object.keys(versionKeys).reduce((obj: any, key: string) => {
    if (versionsToKeep.includes(key)) {
      obj[key] = versionKeys[key];
      obj[versionKeys[key].program_id] = versionKeys[versionKeys[key].program_id];
    }
    return obj;
  }, {});
  return cleanVersionKeys;
};

export const convertOrderRequestsToDraftState = (orderData: any, allProfiles: any[] = []): any => {
  if (orderData.draft_state) return orderData.draft_state;
  const defaultDraftState = {
    inventory: {},
    inventorySelections: [],
    outputVersions: [],
    profileData: {},
    titleInput: {},
    titleSlice: {},
    values: {
      description: '',
      billable_customer: null,
      managed_by: null,
      po_number: '',
      jobs: []
    },
    jobs: {},
    versionKeys: {},
    versionSelections: []
  };

  // Extract order_requests from the full order data
  const orderRequests = orderData.order_requests || [];

  if (!Array.isArray(orderRequests) || orderRequests.length === 0) {
    return defaultDraftState;
  }

  const primaryVersionIds = orderRequests.map((orderRequest: any) => orderRequest.primary_version_id).filter(Boolean);

  const uniqueProfilesMap = new Map();
  orderRequests.reverse().forEach((orderRequest: any) => {
    const profileId = orderRequest.profile_id;
    if (!uniqueProfilesMap.has(profileId)) {
      uniqueProfilesMap.set(profileId, {
        profile_instance: orderRequest.profile_instance,
        due_date: orderRequest.due_date,
        embargo_date: orderRequest.embargo_date,
        instance_inputs: orderRequest.instance_inputs,
        workflow_version: orderRequest.workflow_version,
        order_profile_inputs: orderRequest.order_profile_inputs
      });
    }
  });

  const uniqueProfiles = Array.from(uniqueProfilesMap.values());

  const formJobs = uniqueProfiles.map((profileData: any) => {
    const fullProfile = allProfiles.find((p: any) => p.id === profileData.profile_instance?.id);

    const profileForForm = fullProfile
      ? {
          ...fullProfile,
          data: profileData.profile_instance?.data || fullProfile.data,
          label: fullProfile.name,
          value: fullProfile.slug || fullProfile.id
        }
      : {
          ...profileData.profile_instance,
          label: `Profile ${profileData.profile_instance?.id}`,
          value: profileData.profile_instance?.id
        };
    if (!profileForForm.data.workflow_type && fullProfile) profileForForm.data.workflow_type = fullProfile.data.workflow_type;

    return {
      customer: fullProfile?.customer_name || '',
      profile: profileForForm,
      due_date: profileData.due_date,
      embargo_date: profileData.embargo_date || null,
      order_profile_inputs: profileData.order_profile_inputs || undefined,
      title_inputs: {}
    };
  });
  const generatedJobs = orderRequests.reduce((generatedJobs: any, orderRequest: any) => {
    const id = nanoid();
    generatedJobs[id] = {
      id: id,
      ...orderRequest,
      profile: formJobs.find((j) => j.profile.id === orderRequest.profile_instance?.id)?.profile || undefined,
      customer: formJobs.find((j) => j.profile.id === orderRequest.profile_instance?.id)?.customer || undefined
    };
    return generatedJobs;
  }, {});

  const firstOrderRequest = orderRequests[0];

  return {
    ...defaultDraftState,
    step: uniqueProfiles[0].profile_instance?.data ? 2 : 0,
    profileData: { ...firstOrderRequest.profile_instance?.data, ...firstOrderRequest.profile_instance },
    primaryVersionIds,
    values: {
      description: orderData.description || '',
      po_number: orderData.po_number || '',
      billable_customer: orderData.billable_customer || null,
      managed_by: orderData.managed_by || orderData.managed_by_user || null,
      jobs: formJobs
    },
    jobs: generatedJobs
  };
};

export function applyTemplateToOrderSpec({
  templateInputs,
  currentOrderSpecInputs,
  orderSpecSchema
}: {
  templateInputs?: Record<string, any>;
  currentOrderSpecInputs?: Record<string, any>;
  orderSpecSchema?: RJSFSchema;
}) {
  if (!templateInputs || !orderSpecSchema?.properties) {
    return {
      updatedOrderSpecInputs: currentOrderSpecInputs ?? {},
      didChange: false
    };
  }

  const allowedKeys = Object.keys(orderSpecSchema.properties);
  const filtered: Record<string, any> = {};

  for (const key of allowedKeys) {
    if (templateInputs[key] !== undefined) {
      filtered[key] = templateInputs[key];
    }
  }

  if (Object.keys(filtered).length === 0) {
    return {
      updatedOrderSpecInputs: currentOrderSpecInputs ?? {},
      didChange: false
    };
  }

  const base = currentOrderSpecInputs ?? {};
  const merged = deepMerge(filtered, base);
  const didChange = !isEqual(merged, currentOrderSpecInputs);

  return {
    updatedOrderSpecInputs: merged,
    didChange
  };
}

export function applyTemplateToOrderProfile({
  templateInputs,
  currentOrderProfileInputs,
  orderProfileSchema,
  shouldReset = false
}: {
  templateInputs?: Record<string, any>;
  currentOrderProfileInputs?: Record<string, any>;
  orderProfileSchema?: RJSFSchema;
  shouldReset?: boolean;
}) {
  if (!templateInputs || !orderProfileSchema?.properties) {
    return {
      updatedOrderProfileInputs: currentOrderProfileInputs ?? {},
      didChange: false
    };
  }

  const allowedKeys = Object.keys(orderProfileSchema.properties);
  const filtered: Record<string, any> = {};

  for (const key of allowedKeys) {
    if (templateInputs[key] !== undefined) {
      filtered[key] = templateInputs[key];
    }
  }

  if (Object.keys(filtered).length === 0) {
    return {
      updatedOrderProfileInputs: currentOrderProfileInputs ?? {},
      didChange: false
    };
  }
  const base = currentOrderProfileInputs ?? {};
  const merged = shouldReset ? filtered : deepMerge(filtered, base);
  const didChange = !isEqual(merged, currentOrderProfileInputs);

  return {
    updatedOrderProfileInputs: merged,
    didChange
  };
}

const calculateAdditionalProfileFieldValues = (job: any, addlFields: OrderPlacementConfig['addlFields']) => {
  return Object.keys(addlFields?.profileDetails || {}).reduce((acc: { [key: string]: string | number; }, key) => {
    if (job[key]) {
      if (typeof job[key] === 'object' && 'id' in job[key]) {
        acc[key] = job[key].id;
      } else {
        acc[key] = job[key];
      }
    }
    return acc;
  }, {});
};